import { getLogger, getTraceInfo } from '@storefront/logging';
import { ONE_DAY_IN_MS } from '@storefront/utils/utils';
import axios from 'axios';
import { TokenSet } from 'next-auth';
import QueryString from 'qs';

import {
  AccountNodeResponse,
  CRRDomainsResponse,
  CRRNodeResponse,
  OAuthContext,
  TenantType,
  WellKnownConfig,
} from './auth.types';

const logger = getLogger('auth');

interface CachedRequest {
  value: WellKnownConfig;
  time: number;
}
let cache: CachedRequest = undefined;

export const parseJWT = (jwt: string) =>
  JSON.parse(Buffer.from(jwt.split('.')[1], 'base64').toString());

export const fetchAuthEndpoints = async (): Promise<WellKnownConfig> => {
  const url = process.env.AUTHZ_WELL_KNOWN;
  if (cache) {
    const { time, value } = cache;
    const date = Date.now();
    if (Math.abs(date - time) < ONE_DAY_IN_MS) return value;
  }
  const { data } = await axios.get<WellKnownConfig>(url);
  cache = { value: data, time: Date.now() };
  return data;
};

const getErrorMsg = (err: unknown) => {
  if (axios.isAxiosError(err)) {
    const { response, message, code, config } = err;
    const status = response?.status;
    const url = config?.url;
    let dataStr = '';
    try {
      const data = response?.data;
      dataStr = typeof data === 'string' ? data : JSON.stringify(data);
    } catch {
      dataStr = '[unserializable response data]';
    }
    return `AxiosError ${code ?? ''} ${message} - ${url} -> ${
      status ?? ''
    } ; data: ${dataStr}`.trim();
  }
  if (err instanceof Error) {
    return err.message;
  }
  try {
    return JSON.stringify(err);
  } catch {
    return 'Unknown error';
  }
};

const fetchTenantInformation = async (
  orglessToken: string,
  tenantId: string
): Promise<CRRNodeResponse> => {
  const url = `${process.env.STRATUS_CRR_SERVICE_URL}/node/${tenantId}`;
  const { data } = await axios.get<CRRNodeResponse>(url, {
    headers: {
      Authorization: `Bearer ${orglessToken}`,
    },
  });
  return data;
};

export const getAllTenantsInformation = async (
  orglessToken: string
): Promise<CRRNodeResponse[]> => {
  const tenants = await fetchAvailableTenants(orglessToken);
  const tenantInfoPromises = tenants.content.map((tenantId) =>
    fetchTenantInformation(orglessToken, tenantId)
  );
  return Promise.all(tenantInfoPromises);
};

const fetchAvailableTenants = async (
  orglessToken: string
): Promise<CRRDomainsResponse> => {
  const url = process.env.STRATUS_CRR_SERVICE_URL + '/person/domains';
  const { data } = await axios.get<CRRDomainsResponse>(url, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${orglessToken}`,
    },
    responseType: 'json',
  });
  return data;
};

export const getTenant = async (
  token: TokenSet,
  tenantType: TenantType,
  selectedTenantId?: string | undefined,
  enableTenantSelector: boolean = false
): Promise<CRRNodeResponse | CRRNodeResponse[]> => {
  const orglessToken = token.access_token;
  const tenants = await fetchAvailableTenants(orglessToken);

  if (selectedTenantId && enableTenantSelector) {
    return fetchTenantInformation(orglessToken, selectedTenantId);
  }

  const fetchedTenants = await Promise.all(
    tenants.content.map((tenantId) =>
      fetchTenantInformation(orglessToken, tenantId)
    )
  );

  if (enableTenantSelector && fetchedTenants.length > 1) {
    return fetchedTenants;
  }

  const fetchedTenant = fetchedTenants.find(
    (fetchedTenant) => fetchedTenant.type === tenantType
  );
  const onlyBusiness = hasOnlyBusiness(tenants.totalElements, fetchedTenants);

  if (!fetchedTenant && !onlyBusiness && tenantType === TenantType.PERSONAL) {
    const personAccount = await createPersonalAccount(token, {
      accountName: 'My Printers',
      language: 'en',
      country: 'US',
    });
    const tenantId = personAccount.resourceId;
    return fetchTenantInformation(orglessToken, tenantId);
  }
  return fetchedTenant;
};

export const exchangeToken = async (
  token: TokenSet,
  tenantType: TenantType,
  tenant?: CRRNodeResponse | CRRNodeResponse[] | string
): Promise<TokenSet> => {
  logger.info('Starting exchange');

  let fetchedTenant;
  if (!tenant) {
    fetchedTenant = await getTenant(token, tenantType);
  } else {
    fetchedTenant = tenant;
  }

  if (!fetchedTenant) {
    logger.info('Tenant was not found.');
    return { ...token };
  }
  const encodedAuth = Buffer.from(
    `${process.env.CLIENT_ID}:${process.env.CLIENT_SECRET}`
  ).toString('base64');

  let resolvedTenant: string;
  if (typeof fetchedTenant === 'string') {
    resolvedTenant = fetchedTenant;
  } else {
    resolvedTenant = (
      Array.isArray(fetchedTenant) ? fetchedTenant[0] : fetchedTenant
    )?.nodeId;
  }
  try {
    const body = {
      grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
      subject_token: token.access_token,
      subject_token_type: 'urn:ietf:params:oauth:token-type:access_token',
      tenant_id: resolvedTenant,
    };
    const authzEndpoints = await fetchAuthEndpoints();
    const tokenEndpoint = authzEndpoints['token_endpoint'];

    const exchangedTokenSet = await axios.post<TokenSet>(
      tokenEndpoint,
      QueryString.stringify(body),
      {
        headers: {
          Authorization: `Basic ${encodedAuth}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    logger.info('Returning exchanged token');
    return { ...token, ...exchangedTokenSet.data };
  } catch (err) {
    if (err instanceof Error) {
      logger.error(err.message);
    }
    logger.error({
      msg: 'Error when exchanging token',
      error: getErrorMsg(err),
    });
  }
};

// Error handling is at the code that calls this function
export const retrieveToken = async (
  context: OAuthContext,
  redirect_uri: string
) => {
  const authzEndpoints = await fetchAuthEndpoints();
  const tokenEndpoint = authzEndpoints['token_endpoint'];
  const encodedInfo = Buffer.from(
    `${process.env.CLIENT_ID}:${process.env.CLIENT_SECRET}`
  ).toString('base64');

  logger.info(
    `callbackUrl from context: ${context?.provider?.callbackUrl}; from param: ${redirect_uri}`
  );

  const requestBody = {
    grant_type: 'authorization_code',
    redirect_uri,
    code: context.params?.code,
    state: context.params.state,
  };
  logger.info('Retrieving token');
  const { data } = await axios.post(
    tokenEndpoint,
    QueryString.stringify(requestBody),
    {
      headers: {
        Accept: 'application/json',
        Authorization: `Basic ${encodedInfo}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }
  );
  const tokens: TokenSet = {
    access_token: data.access_token,
    token_type: data.token_type,
    id_token: data.id_token,
    refresh_token: data.refresh_token,
    scope: data.scope,
    access_token_expires_in: data.expires_in,
  };
  return tokens;
};

export const createPersonalAccount = async (
  token: TokenSet,
  requestParams: { accountName?: string; country?: string; language?: string }
): Promise<AccountNodeResponse> => {
  try {
    const orglessToken = token.access_token;
    const idToken = token.id_token;
    const { accountName, country, language } = requestParams;

    const requestBody = {
      accountName: accountName,
      countrySet: [country],
      language,
      type: TenantType.PERSONAL,
      idToken: idToken,
    };
    const url = process.env.UCDE_API_URL + '/accountmgtsvc/accounts';
    const { data } = await axios.post<AccountNodeResponse>(url, requestBody, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${orglessToken}`,
        'Content-Type': 'application/json',
      },
    });
    logger.info('Account created successfully');
    return data;
  } catch (err) {
    logger.error({
      msg: 'Error when creating personal account',
      error: getErrorMsg(err),
    });
  }
};

export const refreshAccessToken = async (token: TokenSet) => {
  const encodedInfo = Buffer.from(
    `${process.env.CLIENT_ID}:${process.env.CLIENT_SECRET}`
  ).toString('base64');

  const requestBody = {
    grant_type: 'refresh_token',
    refresh_token: token.refresh_token,
  };
  try {
    const authzEndpoints = await fetchAuthEndpoints();
    const tokenEndpoint = authzEndpoints['token_endpoint'];
    const { data } = await axios.post(
      tokenEndpoint,
      QueryString.stringify(requestBody),
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Basic ${encodedInfo}`,
          'Content-Type': 'application/x-www-form-urlencoded',
          'x-request-id': getTraceInfo().trace_id,
        },
      }
    );
    const parsedToken = parseJWT(data.access_token);
    return {
      ...token,
      access_token: data.access_token,
      access_token_expires_in: parsedToken.ca_exp,
      refresh_token: data.refresh_token ?? token.refresh_token,
    };
  } catch (err) {
    logger.error({
      msg: 'Error during token refresh',
      error: getErrorMsg(err),
    });
  }
};

const hasOnlyBusiness = (
  totalElements: number,
  fetchedTenants: CRRNodeResponse[]
) => {
  const businessTenant = fetchedTenants.find(
    (businessTenant) => businessTenant.type === TenantType.BUSINESS
  );
  return totalElements == 1 && businessTenant;
};

interface ResolveTenantSelectionResult {
  tokens?: TokenSet;
  fetchedTenants?: CRRNodeResponse | CRRNodeResponse[];
  shouldReturn: boolean;
}

interface Logger {
  info(message: string): void;
}

export async function resolveTenantSelection(
  tokens: TokenSet,
  selectedTenantId: string | undefined,
  enableTenantSelector: boolean,
  logger: Logger
): Promise<ResolveTenantSelectionResult> {
  if (!enableTenantSelector)
    return { fetchedTenants: undefined, shouldReturn: false };

  const fetchedTenants = await getTenant(
    tokens,
    TenantType.PERSONAL,
    selectedTenantId,
    enableTenantSelector
  );

  if (Array.isArray(fetchedTenants)) {
    logger.info('Multiple tenants found, marking token for tenant selection');
    return { fetchedTenants, tokens: { ...tokens }, shouldReturn: true };
  }

  return { fetchedTenants, shouldReturn: false };
}
